/**
 * MarketingSection.tsx - Marketing and Redirect Configuration Section
 *
 * This component handles marketing settings including redirect behavior,
 * promoter goals, and post-campaign actions for fundraiser optimization.
 *
 * Key Features:
 * - Redirect behavior configuration for visitors
 * - Promoter goal setting and management
 * - Goal achievement behavior settings
 * - Website and mobile redirect options
 * - Fundraiser selection for specific redirects
 * - Marketing optimization settings
 *
 * Redirect Options:
 * - None: No redirect, standard fundraiser behavior
 * - Other Fundraisers: Redirect to related fundraisers
 * - Website: Redirect to external website URL
 * - Mobile Pages: Redirect to specific mobile pages
 *
 * Redirect Types for Other Fundraisers:
 * - Closest to Goal: Redirect to fundraiser nearest completion
 * - Any Active: Redirect to any currently active fundraiser
 * - Specific: Redirect to manually selected fundraisers
 *
 * Goal Management:
 * - Default Promoter Goal: Starting goal for new promoters
 * - Goal Achievement Actions: What happens when goal is reached
 * - Goal Increase Options: Automatic goal increases
 * - Close on Achievement: Whether to close when goal is met
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, fundraiser selector, URL validation
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, X, Check, ChevronDown, Plus } from "lucide-react";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { FundraiserSelector } from "../../shared/FundraiserSelector";
import { MarketingSectionRewardsByAmount } from "./MarketingSectionRewardsByAmount";
import { MarketingSectionRewardsByRank } from "./MarketingSectionRewardsByRank";
import { MarketingSectionSponsors } from "./MarketingSectionSponsors";
import { FundRaiserStatus, RedirectOtherFundraiserType } from "../../../enums";
import { CreateFundraiserModel } from "../../../models";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const CompactBlock = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const BlockTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns || 1}, 1fr)`};
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VisualDivider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 24px 0;
`;

const FieldLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const CurrencyInput = styled.div`
  position: relative;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
`;

const CurrencyField = styled.input`
  padding: 16px 16px 16px 40px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    color: #1e293b;
  }

  &:not(:placeholder-shown) {
    color: #1e293b;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.4;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 30px;
`;

const CompactRadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin: 8px 0 30px 0;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 16px;
  color: #374151;
  font-weight: 400;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const CompactCheckboxOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-left: 0;
  margin-top: 0px;
  margin-bottom: 12px;
`;

const LastCompactCheckboxOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-left: 0;
  margin-top: 0px;
  margin-bottom: 30px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const URLInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 50%;
`;

const PreviewButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const MobilePagePreviewButton = styled.button`
  margin-left: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const MobilePageWithPreview = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const GoalIncreaseDropdown = styled.select`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  color: #1e293b;
  background: transparent;
  cursor: pointer;
  transition: border-color 0.2s ease;
  min-width: 70px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
  pointer-events: none;
  white-space: nowrap;
`;

const CurrencyFieldContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubFormGroup = styled.div`
  margin-left: 32px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddButton = styled.button`
  padding: 12px 16px;
  background: #ffffff;
  color: #10b981;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &:hover {
    background: #f0fdf4;
    border-color: #059669;
  }
`;

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 75%;
`;

const SelectedBadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  width: 100%;
`;

const SelectedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #0ea5e9;
`;

const BadgeRemoveButton = styled.button`
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(3, 105, 161, 0.1);
  }
`;

const MobilePageDropdownContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RedirectSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  width: 50%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DropdownCaptionText = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-top: 12px;
  line-height: 1.4;
  font-style: italic;
  width: 50%;
`;

const MobilePageButton = styled.button<{ isOpen: boolean; hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: #ffffff;
  color: ${(props) => (props.hasValue ? "#1e293b" : "#9ca3af")};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  color: #64748b;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const MobilePageDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10000;
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

const MobilePageSearchContainer = styled.div`
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const MobilePageSearchIcon = styled(Search)`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #94a3b8;
`;

const MobilePageSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #f8fafc;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const MobilePageOption = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.isSelected ? "#f0f9ff" : "transparent")};
  color: #374151;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#f1f5f9")};
  }

  &:focus {
    outline: none;
    background: ${(props) => (props.isSelected ? "#e0f2fe" : "#e0f2fe")};
  }
`;

const SelectedCheckmark = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const DropdownWithAddButton = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const AddFundraiserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  height: 48px;
  background: transparent;
  color: #3b82f6;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #f0f9ff;
    color: #2563eb;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormGroup = styled.div<{ minHeight?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: ${({ minHeight }) => (minHeight ? `${minHeight}px` : "unset")};
`;

const RewardsContainer = styled.div`
  margin-bottom: 24px;
`;

interface MarketingSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  handleFieldBlur: (fieldName: string) => void;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

const mobilePages = [
  { id: "page1", name: "Sample Mobile Page 1" },
  { id: "page2", name: "Sample Mobile Page 2" },
  { id: "page3", name: "Sample Mobile Page 3" },
  { id: "page4", name: "Sample Mobile Page 4" },
  { id: "page5", name: "Sample Mobile Page 5" },
];

export const MarketingSection: React.FC<MarketingSectionProps> = observer(
  ({ builderData, updateBuilderField, handleFieldBlur, onKeyDown }) => {
    const [showMobilePageDropdown, setShowMobilePageDropdown] = useState(false);
    const [mobilePageSearchTerm, setMobilePageSearchTerm] = useState("");
    const [showFundraiserSelector, setShowFundraiserSelector] = useState(false);
    const mobilePageDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          mobilePageDropdownRef.current &&
          !mobilePageDropdownRef.current.contains(event.target as Node)
        ) {
          setShowMobilePageDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const activeFundraisers = fundraiserStore.fundraisers.filter(
      (f) => f.status === FundRaiserStatus.Active && f.id !== builderData.id
    );

    const formatCurrency = (value: string) => {
      const numericValue = value.replace(/[^0-9.]/g, "");
      if (numericValue === "") return "";

      const number = parseFloat(numericValue);
      if (isNaN(number)) return "";

      return number.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    const validatePromoterGoal = () => {
      const promoterGoal = builderData.promoterGoal || 0;
      const fundraiserGoal = builderData.goal || 0;

      if (fundraiserGoal > 0 && promoterGoal > fundraiserGoal) {
        return {
          isValid: false,
          errorMessage: `Max ${formatCurrency(fundraiserGoal.toString())}`,
        };
      }

      return { isValid: true, errorMessage: "" };
    };

    const getFilteredMobilePages = () => {
      return mobilePages.filter((page) =>
        page.name.toLowerCase().includes(mobilePageSearchTerm.toLowerCase())
      );
    };

    const clearMobilePageSearch = () => {
      setMobilePageSearchTerm("");
    };

    const isValidUrl = (url: string): boolean => {
      if (!url) return true;

      const originalUrl = url.trim();
      if (originalUrl === "") return true;

      let processedUrl = originalUrl;
      let startsWithWww = false;

      if (originalUrl.startsWith("www.")) {
        startsWithWww = true;
      }

      if (
        !originalUrl.startsWith("http://") &&
        !originalUrl.startsWith("https://")
      ) {
        processedUrl = `https://${originalUrl}`;
      }

      try {
        const urlObj = new URL(processedUrl);
        const hostname = urlObj.hostname;

        if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
          return false;
        }

        if (
          hostname === "localhost" ||
          /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
        ) {
          return true;
        }

        const domainRegex =
          /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
        if (!domainRegex.test(hostname)) {
          return false;
        }

        const dotCount = hostname.split(".").length - 1;
        if (startsWithWww) {
          return dotCount >= 2;
        } else {
          return dotCount >= 1;
        }
      } catch (e) {
        return false;
      }
    };

    // Auto-open modal if specific fundraisers is selected but no fundraisers are selected
    // React.useEffect(() => {
    //   if (
    //     builderData.redirectOtherFundraiserType ===
    //       RedirectOtherFundraiserType.Specific &&
    //     (!builderData.redirectSpecificFundraiserIds ||
    //       builderData.redirectSpecificFundraiserIds.length === 0)
    //   ) {
    //     setShowFundraiserSelector(true);
    //   }
    // }, [builderData.redirectOtherFundraiserType]);

    const handleFundraiserSelectionChange = (fundraiserIds: string[]) => {
      updateBuilderField("redirectSpecificFundraiserIds", fundraiserIds);
    };

    const removeFundraiserFromSelection = (fundraiserId: string) => {
      const current = builderData.redirectSpecificFundraiserIds || [];
      const updated = current.filter((id: string) => id !== fundraiserId);
      updateBuilderField("redirectSpecificFundraiserIds", updated);
    };

    const handleWebsitePreview = () => {
      if (
        builderData.redirectWebsiteUrl &&
        isValidUrl(builderData.redirectWebsiteUrl)
      ) {
        let url = builderData.redirectWebsiteUrl;
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = `https://${url}`;
        }
        window.open(url, "_blank");
      }
    };

    const handleMobilePagePreview = () => {
      if (builderData.redirectMobilePage) {
        // In a real app, this would open the actual mobile page URL
        // For demo purposes, we'll just show an alert
        const selectedPage = mobilePages.find(
          (p) => p.id === builderData.redirectMobilePage
        );
        const pageUrl = `https://example.com/mobile/${builderData.redirectMobilePage}`;
        window.open(pageUrl, "_blank");
      }
    };

    return (
      <Section>
        <CompactBlock>
          <BlockTitle>Goals</BlockTitle>
          <FormGrid columns={2} style={{ marginBottom: "16px" }}>
            <FormGroup>
              <FieldLabel htmlFor="goal">Fundraiser Goal</FieldLabel>
              <CurrencyInput>
                <CurrencySymbol>$</CurrencySymbol>
                <CurrencyField
                  id="goal"
                  type="text"
                  placeholder="0"
                  value={
                    builderData.goal !== undefined && builderData.goal !== null
                      ? formatCurrency(builderData.goal.toString())
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    const goalValue = value ? parseFloat(value) : 0;
                    updateBuilderField("goal", goalValue);

                    // If fundraiser goal is set to 0, also reset promoter goal to 0
                    if (goalValue === 0) {
                      updateBuilderField("promoterGoal", 0);
                    }
                  }}
                  onBlur={() => handleFieldBlur("goal")}
                />
              </CurrencyInput>
              <HelpText style={{ marginBottom: "0px" }}>
                Leave blank for no specific goal
              </HelpText>
            </FormGroup>

            {builderData.goal > 0 && (
              <FormGroup>
                <FieldLabel htmlFor="promoterGoal">Promoter Goal</FieldLabel>
                <CurrencyFieldContainer>
                  <CurrencyInput>
                    <CurrencySymbol>$</CurrencySymbol>
                    <CurrencyField
                      id="promoterGoal"
                      type="text"
                      placeholder="0"
                      style={{
                        borderColor: !validatePromoterGoal().isValid
                          ? "#dc2626"
                          : "#e2e8f0",
                        backgroundColor: !validatePromoterGoal().isValid
                          ? "#fef2f2"
                          : "#ffffff",
                        paddingRight: !validatePromoterGoal().isValid
                          ? "140px"
                          : "16px",
                      }}
                      value={
                        builderData.promoterGoal !== undefined &&
                        builderData.promoterGoal !== null
                          ? formatCurrency(builderData.promoterGoal.toString())
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, "");
                        updateBuilderField(
                          "promoterGoal",
                          value ? parseFloat(value) : 0
                        );
                      }}
                      onBlur={() => handleFieldBlur("promoterGoal")}
                      onKeyDown={(e) => {
                        if (onKeyDown) {
                          onKeyDown(e, true);
                        }
                      }}
                    />
                    {!validatePromoterGoal().isValid && (
                      <ErrorMessage>
                        {validatePromoterGoal().errorMessage}
                      </ErrorMessage>
                    )}
                  </CurrencyInput>
                </CurrencyFieldContainer>
                <HelpText>
                  Default goal amount for new promoters (optional)
                </HelpText>
              </FormGroup>
            )}
          </FormGrid>
          <br></br>
          <FormGroup style={{ marginBottom: "0px", marginTop: "8px" }}>
            <Label style={{ marginBottom: "8px" }}>
              Close fundraiser once goal is achieved
            </Label>
            <CompactRadioRow
              style={{
                marginTop: "8px",
                alignItems: "flex-start",
                marginBottom: "8px",
              }}
            >
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="closeOnGoalAchieved"
                  value="yes"
                  checked={builderData.closeOnGoalAchieved || false}
                  onChange={() => {
                    updateBuilderField("closeOnGoalAchieved", true);
                    updateBuilderField("increaseGoalOnAchieved", false);
                  }}
                />
                Yes
              </RadioOption>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="closeOnGoalAchieved"
                  value="no"
                  checked={!builderData.closeOnGoalAchieved}
                  onChange={() => {
                    updateBuilderField("closeOnGoalAchieved", false);
                    updateBuilderField("increaseGoalOnAchieved", true);
                    if (!builderData.increaseGoalPercentage) {
                      updateBuilderField("increaseGoalPercentage", "10");
                    }
                  }}
                />
                No
              </RadioOption>

              {/* Show checkbox option when No is selected - now on same row */}
              {!builderData.closeOnGoalAchieved && (
                <div
                  style={{
                    marginTop: "0px",
                    marginLeft: "30px",
                    minHeight: "24px",
                    alignItems: "center",
                    marginBottom: "8px",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <Checkbox
                    type="checkbox"
                    checked={builderData.increaseGoalOnAchieved !== false}
                    onChange={(e) =>
                      updateBuilderField(
                        "increaseGoalOnAchieved",
                        e.target.checked
                      )
                    }
                    style={{ marginTop: "0px" }}
                  />
                  <CheckboxLabel
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "0px",
                    }}
                  >
                    {builderData.increaseGoalOnAchieved !== false ? (
                      <>
                        Increase goal by{" "}
                        <GoalIncreaseDropdown
                          value={builderData.increaseGoalPercentage || "10"}
                          onChange={(e) =>
                            updateBuilderField(
                              "increaseGoalPercentage",
                              e.target.value
                            )
                          }
                        >
                          <option value="5">5%</option>
                          <option value="10">10%</option>
                          <option value="15">15%</option>
                          <option value="20">20%</option>
                          <option value="25">25%</option>
                        </GoalIncreaseDropdown>{" "}
                        when goal is met
                      </>
                    ) : (
                      "Increase goal"
                    )}
                  </CheckboxLabel>
                </div>
              )}
            </CompactRadioRow>
          </FormGroup>
        </CompactBlock>

        <VisualDivider />

        <CompactBlock>
          <BlockTitle>Recruit promoters</BlockTitle>
          <FormGroup style={{ marginBottom: "0px" }}>
            <CompactCheckboxOption
              style={{
                marginTop: "0px",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Checkbox
                type="checkbox"
                checked={builderData.allowPromoterInvites !== false}
                onChange={(e) =>
                  updateBuilderField("allowPromoterInvites", e.target.checked)
                }
                style={{ marginTop: "0px" }}
              />
              <CheckboxLabel>
                Allow promoters to recruit new promoters to help them fundraise
              </CheckboxLabel>
            </CompactCheckboxOption>

            {builderData.allowPromoterInvites !== false && (
              <LastCompactCheckboxOption
                style={{ alignItems: "center", marginBottom: "8px" }}
              >
                <Checkbox
                  type="checkbox"
                  checked={builderData.includeRecruitedActivity !== false}
                  onChange={(e) =>
                    updateBuilderField(
                      "includeRecruitedActivity",
                      e.target.checked
                    )
                  }
                  style={{ marginTop: "0px" }}
                />
                <CheckboxLabel>
                  Include recruited promoter activity in promoter goals
                </CheckboxLabel>
              </LastCompactCheckboxOption>
            )}
          </FormGroup>
        </CompactBlock>

        <VisualDivider />

        <RewardsContainer>
          <BlockTitle>Rewards</BlockTitle>
          <div
            style={{ fontSize: "14px", color: "#64748b", marginBottom: "24px" }}
          >
            Offer rewards to promoters to drive donations and engagement.{" "}
            <b>By Amount:</b> Earn <u>multiple</u> rewards when each donation
            amount is achieved. <b>By Rank:</b> Earn a <u>single</u> reward for
            the highest rank achieved.
          </div>

          {/* Show add buttons based on what doesn't exist yet */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              paddingTop: "10px",
              paddingBottom: "10px",
            }}
          >
            {(builderData.rewards || []).length === 0 && (
              <button
                type="button"
                onClick={() => {
                  const newReward = {
                    id: Date.now().toString(),
                    level: "",
                    reward: "",
                    isEditing: true,
                    isNew: true,
                  };
                  updateBuilderField("rewards", [
                    ...(builderData.rewards || []),
                    newReward,
                  ]);
                }}
                style={{
                  background: "transparent",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                  color: "#3b82f6",
                  fontSize: "16px",
                  fontWeight: "400",
                  cursor: "pointer",
                  padding: "12px 24px",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f0f9ff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                + Reward by Amount
              </button>
            )}

            {(builderData.ranks || []).length === 0 && (
              <button
                type="button"
                onClick={() => {
                  const currentRanks = (builderData.ranks || [])
                    .map((r: any) => r.rank?.toString())
                    .filter(Boolean);
                  const nextRankValue = currentRanks.length === 0 ? "1" : "2"; // Simple logic for demo
                  const newRank = {
                    id: Date.now().toString(),
                    rank: nextRankValue,
                    reward: "",
                    minimum: 1,
                    isEditing: true,
                    isNew: true,
                  };
                  updateBuilderField("ranks", [
                    ...(builderData.ranks || []),
                    newRank,
                  ]);
                }}
                style={{
                  background: "transparent",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                  color: "#3b82f6",
                  fontSize: "16px",
                  fontWeight: "400",
                  cursor: "pointer",
                  padding: "12px 24px",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f0f9ff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                + Reward by Rank
              </button>
            )}
          </div>

          <MarketingSectionRewardsByAmount
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            onSave={() => {
              // This will be handled by the parent component's save logic
              // The toast will be shown when the entire builder is saved
            }}
          />

          <MarketingSectionRewardsByRank
            builderData={builderData}
            updateBuilderField={updateBuilderField}
            onSave={() => {
              // This will be handled by the parent component's save logic
              // The toast will be shown when the entire builder is saved
            }}
          />
        </RewardsContainer>

        <VisualDivider />

        <MarketingSectionSponsors
          builderData={builderData}
          updateBuilderField={updateBuilderField}
          onKeyDown={onKeyDown}
        />

        {/* Fundraiser Selector Modal */}
        <FundraiserSelector
          isModal={true}
          isVisible={showFundraiserSelector}
          onClose={() => setShowFundraiserSelector(false)}
          selectedFundraiserIds={
            builderData.redirectSpecificFundraiserIds || []
          }
          onSelectionChange={handleFundraiserSelectionChange}
          excludeFundraiserId={builderData.id}
          status={[FundRaiserStatus.Active]}
          availability={["current", "future"]}
          multiple="yes"
        />
      </Section>
    );
  }
);
